// Hooks do perfil para a IA e da memória da empresa.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BusinessProfileDto, ContextFactDto, FactsResponse, ProfileExtractResponse, ProfileResponse } from '@impulso/shared/api/ai'
import { api } from '../../lib/api'

export const contextKeys = {
  profile: (companyId: string) => ['context', companyId, 'profile'] as const,
  facts: (companyId: string) => ['context', companyId, 'facts'] as const,
}

export function emptyProfile(): BusinessProfileDto {
  return { products: [], differentials: [], toneOfVoice: null, idealCustomer: null, neverSay: [], seasonality: null, serviceRadiusKm: null, delivers: null, website: null, instagram: null }
}

export function useProfile(companyId: string) {
  return useQuery({ queryKey: contextKeys.profile(companyId), queryFn: () => api.get<ProfileResponse>(`/companies/${companyId}/profile`) })
}

export function useSaveProfile(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: BusinessProfileDto & { fromExtraction?: boolean }) => api.put<ProfileResponse>(`/companies/${companyId}/profile`, body),
    onSuccess: (r) => qc.setQueryData(contextKeys.profile(companyId), r),
  })
}

export function useExtractProfile(companyId: string) {
  return useMutation({
    mutationFn: (body: { website?: string | null; instagram?: string | null }) => api.post<ProfileExtractResponse>(`/companies/${companyId}/profile/extract`, body),
  })
}

export function useFacts(companyId: string) {
  return useQuery({ queryKey: contextKeys.facts(companyId), queryFn: () => api.get<FactsResponse>(`/companies/${companyId}/facts`) })
}

export function useAddFact(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (text: string) => api.post<{ item: ContextFactDto | null }>(`/companies/${companyId}/facts`, { text, kind: 'preference' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: contextKeys.facts(companyId) }),
  })
}

export function useRemoveFact(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<{ ok: true }>(`/companies/${companyId}/facts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: contextKeys.facts(companyId) }),
  })
}
